import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, useMediaQuery, useTheme, Badge, IconButton, Tooltip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GetAppIcon from '@mui/icons-material/GetApp';
import CategoriesMenu from './CategoriesMenu';
import useCart from '../hooks/useCart';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { cartCount } = useCart();
  
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Слушаем событие beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    // Слушаем событие appinstalled
    const handleAppInstalled = () => {
      setShowInstallButton(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('Пользователь принял установку приложения');
      } else {
        console.log('Пользователь отклонил установку приложения');
      }
      
      setDeferredPrompt(null);
      setShowInstallButton(false);
    }
  };


  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        backgroundColor: 'white', 
        color: 'black', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        zIndex: theme.zIndex.drawer + 1,
        transition: 'box-shadow 0.3s ease-in-out'
      }}
    >
      <Toolbar>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            flexGrow: 1 
          }}
          onClick={() => navigate('/')}
        >
          <img 
            src="/zara-logo.jpg" 
            alt="ZARA Logo" 
            style={{ 
              height: isMobile ? 24 : 32, 
              width: 'auto',
              marginRight: isMobile ? 8 : 16
            }} 
          />
          <Typography 
            variant={isMobile ? "body1" : "h6"} 
            component="div" 
            sx={{ 
              fontWeight: 300, 
              letterSpacing: isMobile ? 0.5 : 1,
              fontSize: isMobile ? '0.9rem' : '1.1rem'
            }}
          >
            {isMobile ? 'ZARA в Россию buyer_olesia' : 'ZARA в Россию через buyer_olesia'}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, alignItems: 'center' }}>
          {/* Меню категорий - всегда видимо */}
          <CategoriesMenu />
          
          {/* Главная страница - всегда видима */}
          <Button 
            color="inherit" 
            onClick={() => navigate('/')}
            sx={{ 
              textTransform: 'none', 
              fontSize: isMobile ? '0.9rem' : '1.1rem',
              fontWeight: location.pathname === '/' ? 600 : 400,
              borderBottom: location.pathname === '/' ? '2px solid black' : 'none',
              minWidth: 'auto',
              px: isMobile ? 1 : 2
            }}
          >
            Главная
          </Button>
          
          {/* Просмотренные товары */}
          <Button 
            color="inherit" 
            onClick={() => navigate('/viewed')}
            sx={{ 
              textTransform: 'none', 
              fontSize: isMobile ? '0.9rem' : '1.1rem',
              fontWeight: location.pathname === '/viewed' ? 600 : 400,
              borderBottom: location.pathname === '/viewed' ? '2px solid black' : 'none',
              minWidth: 'auto',
              px: isMobile ? 1 : 2,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
          >
            <VisibilityIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
         
          </Button>
          
          {/* Кнопка установки приложения - показывается только на мобильных и при наличии события установки */}
          {showInstallButton && (isMobile || deferredPrompt) && (
            <Tooltip title="Установить приложение">
              <IconButton
                color="inherit"
                onClick={handleInstall}
                sx={{ 
                  minWidth: 'auto',
                  px: isMobile ? 1 : 2,
                  color: '#2196F3',
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 0.1)'
                  }
                }}
              >
                <GetAppIcon sx={{ 
                  fontSize: isMobile ? 20 : 24 
                }} />
              </IconButton>
            </Tooltip>
          )}
          
          {/* Корзина */}
          <IconButton
            color="inherit"
            onClick={() => navigate('/cart')}
            sx={{ 
              position: 'relative',
              minWidth: 'auto',
              px: isMobile ? 1 : 2
            }}
          >
            <Badge 
              badgeContent={cartCount} 
              color="primary"
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontSize: isMobile ? '0.7rem' : '0.8rem',
                  minWidth: isMobile ? 16 : 20,
                  height: isMobile ? 16 : 20
                }
              }}
            >
              <ShoppingCartIcon sx={{ 
                fontSize: isMobile ? 20 : 24 
              }} />
            </Badge>
          </IconButton>

        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
